import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { MirageConstants } from "@shared/mirage/mirageConstants";
import L from "leaflet";
import { useMemo, useState } from "react";
import { useRef, useEffect } from "react";
import { GaiaMapWmsLayer, MapWmsLayer, TileLayerOptions } from "../GaiaMap";
import "proj4leaflet";
import { MapViewHelper } from "@mirage/components/MapView/MapViewHelper";

interface InitializeMapProps {
    mapContainer: React.RefObject<HTMLDivElement>;
    tileLayer?: TileLayerOptions;
    featureGroup: React.MutableRefObject<L.FeatureGroup>;
    center?: L.LatLngExpression | null;
    wms?: GaiaMapWmsLayer;
    setWmsLayers?: React.Dispatch<React.SetStateAction<MapWmsLayer[]>>;
    setWmsLegendHeader: React.Dispatch<React.SetStateAction<string | null>>;
    zoom?: number;
}

const MAX_ZOOM = 15;

export const useInitializeMap = (props: InitializeMapProps): L.DrawMap | null => {
    const { mapContainer, tileLayer, featureGroup, center, wms, setWmsLayers, setWmsLegendHeader, zoom } = props;
    const [map, setMap] = useState<L.DrawMap | null>(null);
    const mapRef = useRef<L.DrawMap | null>(null);
    const masterLayer: TileLayerOptions = useMemo(() => {
        if (!tileLayer || !tileLayer.layerUrl) {
            console.error("No master layer specified from the client settings");
            return { layerUrl: MirageConstants.MasterLayerUrl, attribution: MirageConstants.MasterLayerAttribution };
        }
        const attribution = tileLayer.attribution || MirageTheme.map.attributions.OSAttribution;
        if (tileLayer.attribution !== attribution) {
            tileLayer.attribution = attribution;
        }
        return tileLayer;
    }, [tileLayer]);

    useEffect(() => {
        const initializeMap = async () => {
            if (!mapContainer.current) {
                console.error("Map container is not defined");
                return;
            }

            const defaultCenter: L.LatLngExpression = [51.505, -0.09];

            mapRef.current = L.map(mapContainer.current, {
                crs: MapViewHelper.GetDefaultCRS(),
                center: center || defaultCenter,
                zoom: MAX_ZOOM,
            });
            setMap(mapRef.current);
            mapRef.current.setView(center || defaultCenter, zoom || MAX_ZOOM);
            mapRef.current.invalidateSize();
            mapRef.current.doubleClickZoom.disable();
            featureGroup.current.addTo(mapRef.current);
            if (!setWmsLayers || !wms || !wms.getWmsCapabilities || !wms.url) {
                return;
            }
            const wmsCapabilities = await wms.getWmsCapabilities({
                url: wms.url,
            });
            if (wmsCapabilities.errorMsg) {
                console.error(wmsCapabilities.errorMsg);
            } else if (wmsCapabilities.capabilities) {
                const wmsLayers = wmsCapabilities.capabilities.layers || [];
                const selectedWmsLayers = wms.showLayers === "all" ? wmsLayers.map((l) => l.name) : wms.showLayers;
                setWmsLayers(
                    wmsLayers.map((l) => {
                        return { layer: l, selected: selectedWmsLayers?.includes(l.name) || false };
                    })
                );
                setWmsLegendHeader(wmsCapabilities.capabilities.mapName || null);
            } else {
                setWmsLayers([]);
                setWmsLegendHeader(null);
            }
        };
        initializeMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }
        L.tileLayer(masterLayer.layerUrl, { maxZoom: MAX_ZOOM, maxNativeZoom: 13, attribution: masterLayer.attribution }).addTo(mapRef.current);
        mapRef.current.attributionControl.setPrefix("Powered by Leaflet");
    }, [map, masterLayer.attribution, masterLayer.layerUrl]);

    return map;
};
